import { useState, type FormEvent } from "react";
import { Trans } from "@lingui/react/macro";
import type { Plan } from "../api.js";

type RetentionPlan = Pick<Plan, "id" | "code" | "name" | "retentionDays">;

// The license plan-change route supplies its authorized mutation as onConfirm.
// Keep the current plan even when inactive: its retention still needs comparing.
export function ChangeLicensePlanDialog({
  currentPlan,
  plans,
  onConfirm,
  onCancel,
}: {
  currentPlan: RetentionPlan;
  plans: RetentionPlan[];
  onConfirm: (planId: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [planId, setPlanId] = useState(currentPlan.id);
  const [confirmedComparison, setConfirmedComparison] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const proposed = plans.find(plan => plan.id === planId);
  const currentDays = currentPlan.retentionDays;
  const proposedDays = proposed?.retentionDays;
  const shorter = proposedDays !== undefined && proposedDays < currentDays;
  const comparison = JSON.stringify([currentPlan.id, currentDays, planId, proposedDays]);
  const confirmed = confirmedComparison === comparison;
  const canConfirm = proposed !== undefined && planId !== currentPlan.id && (!shorter || confirmed);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canConfirm || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(planId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => { if (!submitting) onCancel(); };
  return (
    <div className="modal-backdrop" onClick={cancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="license-plan-title" onClick={event => event.stopPropagation()}>
        <h2 id="license-plan-title"><Trans>Change licence plan</Trans></h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Plan</Trans>
            <select value={planId} disabled={submitting} required onChange={event => {
              setPlanId(event.target.value);
              setConfirmedComparison(null);
              setError(null);
            }}>
              {!plans.some(plan => plan.id === currentPlan.id) && (
                <option value={currentPlan.id}>{currentPlan.code} · {currentPlan.name}</option>
              )}
              {plans.map(plan => <option key={plan.id} value={plan.id}>{plan.code} · {plan.name}</option>)}
            </select>
          </label>
          <p><Trans>Current retention: {currentDays} days.</Trans></p>
          {proposedDays !== undefined && <p><Trans>Proposed retention: {proposedDays} days.</Trans></p>}
          {shorter && (
            <div className="banner warn" role="alert">
              <p><Trans>Shortening retention will permanently delete existing history older than {proposedDays} days at the next retention sweep. Changing plans does not restart the age of existing history.</Trans></p>
              <label>
                <input type="checkbox" checked={confirmed} disabled={submitting} onChange={event => setConfirmedComparison(event.target.checked ? comparison : null)} />
                <Trans>I understand and confirm the shorter retention window.</Trans>
              </label>
            </div>
          )}
          <p className="muted small"><Trans>Extending retention cannot restore history that has already been deleted.</Trans></p>
          {error && <div className="banner err" role="alert">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" disabled={submitting} onClick={cancel}><Trans>Cancel</Trans></button>
            <button type="submit" disabled={!canConfirm || submitting}>{submitting ? <Trans>Saving…</Trans> : <Trans>Confirm plan change</Trans>}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
